import angular from 'angular';
import { prop } from 'micro-fp';
import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';
import { TimesheetStatus } from '@sdk/timesheets/api/apiTypes';

import _toggleFilter from '../shifts/components/filters/toggleFilter';
import config from '../config';

import subnavUrl from './subnav.html';
import laborDataUrl from './labor-data.html';
import timeoffDataUrl from './timeoff-data.html';
import noShowDataUrl from './no-show-data.html';
import payrollDataUrl from './payroll-data.html';
import salesDataUrl from './sales-data.html';
import shiftFeedbackDataUrl from './shift-feedback-data.html';

// Thrown as a bare string by `Utils.raceProofRequest` when a later request for
// the same id supersedes an earlier one.
const STALE_PROMISE_REJECTION = 'Stale promise resolved';

class ReportsBaseCtrl {
  isPayrollReport = false;
  payTypes = [
    { name: 'Hourly wage', id: 'Hourly' },
    { name: 'Annual salary', id: 'Salary' },
  ];

  isAutoClockOutOptions = [
    { name: 'Yes', id: true },
    { name: 'No', id: false },
    { name: 'None', id: null },
  ];

  collapsedGroups = [];
  subnavUrl = subnavUrl;
  laborDataUrl = laborDataUrl;
  timeoffDataUrl = timeoffDataUrl;
  noShowDataUrl = noShowDataUrl;
  payrollDataUrl = payrollDataUrl;
  salesDataUrl = salesDataUrl;
  shiftFeedbackDataUrl = shiftFeedbackDataUrl;
  NONE_STATUS = 'none';

  constructor($injector, $scope) {
    const Authentication = $injector.get('Authentication');
    const AccountService = $injector.get('AccountService');
    const PersonaCollection = $injector.get('PersonaCollection');
    const Utils = $injector.get('Utils');
    const UserUtils = $injector.get('UserUtils');
    const toDateMediumFilter = $injector.get('toDateMediumFilter');
    const uppercaseFirstFilter = $injector.get('uppercaseFirstFilter');
    const $location = $injector.get('$location');
    const $document = $injector.get('$document');
    const ReportsService = $injector.get('ReportsService');
    const DayPartsService = $injector.get('DayPartsService');
    const ShiftUtils = $injector.get('ShiftUtils');
    const SubscriptionUtils = $injector.get('SubscriptionUtils');
    const sdk = $injector.get('sdk');

    Object.assign(this, {
      Authentication,
      AccountService,
      PersonaCollection,
      DayPartsService,
      Utils,
      UserUtils,
      ReportsService,
      ShiftUtils,
      SubscriptionUtils,
      toDateMediumFilter,
      uppercaseFirstFilter,
      sdk,
      $location,
      $document,
      $scope,
    });

    this.statusOptions = [
      { name: 'Pending approval', id: TimesheetStatus.pending },
      isFeatureEnabled(config.TIMESHEET_CONSENT_FLOW_ENABLED, this.Authentication.user?.org.id)
        ? { name: 'Pending consent', id: TimesheetStatus.pendingConsent }
        : null,
      { name: 'Approved', id: TimesheetStatus.approved },
      { name: 'None', id: this.NONE_STATUS },
    ].filter(Boolean);

    const { PAYROLL_PERIOD_TYPES } = this.sdk('constants');
    const payrollPeriodType =
      PAYROLL_PERIOD_TYPES[this.Authentication.user.orgSettings.payrollPeriodType];
    this.modes = [
      { label: 'Day', value: 'day' },
      { label: 'Week', value: 'week' },
      { label: 'Month', value: 'month' },
      { label: `Payroll (${payrollPeriodType})`, value: 'payroll' },
      { label: 'Quarter', value: 'quarter' },
      { label: 'Year', value: 'year' },
      { label: 'Custom', value: 'custom' },
    ];

    this.selectedDate = this.getInitialSelectedDate();

    if (__HEADLESS__) {
      return;
    }

    this.updateLocationsPositionsAndGroups();
    this.filter = {
      locationIds: this.getPreviouslySelectedLocationIdsFilter(),
      positionIds: this.getPreviouslySelectedPositionIdsFilter(),
      statusIds: this.getPreviouslySelectedStatusIdsFilter(),
      userIds: this.getPreviouslySelectedUserIdsFilter(),
      leaveTypeIds: this.getPreviouslySelectedLeaveTypeIdsFilter(),
      groupIds: this.getPreviouslySelectedGroupIdsFilter(),
      tagIds: this.getPreviouslySelectedTagIdsFilter(),
      payTypes: this.getPreviouslySelectedPayTypesFilter(),
      isAutoClockOutIds: this.getPreviouslySelectedIsAutoClockOutIdsFilter(),
      shiftRatings: this.getPreviouslySelectedShiftRatingsFilter(),
      dayPartIds: this.getPreviouslySelectedDayPartIdsFilter(),
    };

    this._filtersOpened = this.Authentication.user.clientcache.reports.filtersOpened;
    this._statsOpened = this.Authentication.user.clientcache.reports.statsOpened;

    this.showDurationsAsDecimals =
      this.Authentication.user.settings.clientFlags.showDurationsAsDecimals;
    this.ReportsService.totalsModeEnabled =
      this.Authentication.user.settings.clientFlags.totalsModeEnabled;

    $scope.$on(
      '$destroy',
      this.PersonaCollection.onChange(() => this.updateLocationsPositionsAndGroups())
    );
  }

  get shouldForceTotalsMode() {
    if (this.sortBy === 'position' || this.sortBy === 'location') {
      return false;
    }

    const detailsIndicators = this.schema?.table.items.filter((c) => c.detailsIndicator);

    return (
      !this.columns ||
      !detailsIndicators?.length ||
      !detailsIndicators.some((c) => {
        const indicatorStatus = this.columns.reportColumns.find(
          (column) => column.property === c.property
        );
        return indicatorStatus?.active;
      })
    );
  }

  get totalsModeEnabled() {
    return this.ReportsService.totalsModeEnabled;
  }

  set totalsModeEnabled(value) {
    this.ReportsService.totalsModeEnabled = value;
  }

  $onInit() {
    const instance = this;
    this.sortByOptions = [
      { label: 'Positions', value: 'position' },
      { label: 'Employees', value: 'user' },
      {
        label: 'Locations',
        value: 'location',
        get hidden() {
          return instance.locations.length <= 1;
        },
      },
      {
        label: 'Groups',
        value: 'group',
        get hidden() {
          return !instance.groups || instance.groups.length <= 1;
        },
      },
      {
        label: 'Date',
        value: 'date',
        get hidden() {
          return !instance.groupByDateEnabled;
        },
      },
    ];

    if (__HEADLESS__) {
      return;
    }

    const { tags } = this.PersonaCollection;
    this.tagsForFilters = this.ShiftUtils.getTagsFilterOptions(tags);
    this.groupsForFilters = this.ShiftUtils.getGroupsFilterOptions(this.groups);

    if (config.ONBOARDING_CHECKLIST_ENABLED) {
      const { clientFlags } = this.Authentication.user.settings;
      this.viewReports = clientFlags.onboardingTrial_viewReports;

      if (!this.viewReports.completed) {
        this.Authentication.saveClientFlags({
          onboardingTrial_viewReports: { key: 'onboardingTrial_viewReports', completed: true },
        });
      }
    }

    this.dayPartsForFilter = this.ShiftUtils.getDayPartsFilterOptions(this.DayPartsService.dayParts);
  }

  get sortBy() {
    if (!this._sortBy) {
      this._sortBy = this.Authentication.user.clientcache.reports.sortBy;

      if (!this.sortByOptions.find((option) => option.value === this._sortBy && !option.hidden)) {
        this._sortBy = 'user';
      }
    }

    return this._sortBy;
  }

  set sortBy(val) {
    this._sortBy = val;
    this.Authentication.user.clientcache.reports.sortBy = val;
    this.Authentication.syncClientCache();

    this.refresh();
  }

  get userGroups() {
    return this.groups;
  }

  filtersEmpty() {
    return Object.entries(this.filter)
      .filter(([key]) => !this.availableFilters || this.availableFilters.includes(key))
      .every(([, array]) => !array.length);
  }

  clearAllFilters() {
    Object.values(this.filter).forEach((array) => (array.length = 0));
    this.syncFiltersCache();
  }

  getInitialSelectedDate() {
    const searchDate = this.$location.search().date;
    if (searchDate) {
      this.setRangeFromCache();
      return this.Utils.getRealDate(searchDate);
    }

    if (!__HEADLESS__ && this.Authentication.user.clientcache.reports.selectedDate) {
      this.setRangeFromCache();
      return this.Utils.getRealDate(this.Authentication.user.clientcache.reports.selectedDate);
    }

    return new Date();
  }

  async setRangeFromCache() {
    if (!__HEADLESS__ && this.Authentication.user.clientcache.reports.selectedRange) {
      this.$scope.$evalAsync(() => {
        this.selectedRange = this.Authentication.user.clientcache.reports.selectedRange;
      });
    }
  }

  updateLocationsPositionsAndGroups() {
    this.locations = [...this.PersonaCollection.locations];
    this.positions = [...this.PersonaCollection.positions];
    this.groups = [...this.PersonaCollection.groups];

    const { positionIds, locationIds } = this.PersonaCollection.me;

    if (!this.Authentication.user.isManager) {
      this.locations = this.locations.filter((loc) => locationIds.includes(loc.id));
      this.positions = this.positions.filter((pos) => positionIds.includes(pos.id));
    } else if (
      !this.Authentication.user.isAdmin &&
      !this.Authentication.user.orgSettings.managersCanSeeSchedulesInOtherLocations
    ) {
      this.locations = this.locations.filter((loc) => locationIds.includes(loc.id));
    }

    this._locationsDict = this.Utils.arrayToDict(this.locations.map(prop('id')));
    this._positionsDict = this.Utils.arrayToDict(this.positions.map(prop('id')));
  }

  get user() {
    return this.Authentication.user;
  }

  get tags() {
    return this.PersonaCollection.tags;
  }

  get dayParts() {
    return this.DayPartsService.dayParts;
  }

  changedShowDurationsAsDecimals() {
    this.Authentication.user.settings.clientFlags.showDurationsAsDecimals =
      this.showDurationsAsDecimals;
    this.Authentication.syncSettings();
  }

  changeTotalsModeEnabled() {
    this.Authentication.user.settings.clientFlags.totalsModeEnabled = this.totalsModeEnabled;
    this.Authentication.syncSettings();
  }

  getPreviouslySelectedLocationIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.locationIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.locationIdsFilter.filter((id) =>
      this.locations.some((loc) => loc.id === id)
    );
  }

  getPreviouslySelectedPositionIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.positionIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.positionIdsFilter.filter((id) =>
      this.positions.some((pos) => pos.id === id)
    );
  }

  getPreviouslySelectedStatusIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.statusIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.statusIdsFilter.filter((id) =>
      this.statusOptions.some((s) => s.id === id)
    );
  }

  getPreviouslySelectedIsAutoClockOutIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.isAutoClockOutIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.isAutoClockOutIdsFilter.filter((id) =>
      this.isAutoClockOutOptions.some((s) => s.id === id)
    );
  }

  getPreviouslySelectedUserIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.userIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.userIdsFilter.filter(
      (id) => this.PersonaCollection.personasDict[id]
    );
  }

  getPreviouslySelectedLeaveTypeIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.leaveTypeIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.leaveTypeIdsFilter;
  }

  getPreviouslySelectedGroupIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.groupIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.groupIdsFilter.filter(
      (id) => this.PersonaCollection.personasDict[id]
    );
  }

  getPreviouslySelectedTagIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.tagIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.tagIdsFilter.filter(
      (id) => this.PersonaCollection.personasDict[id]
    );
  }

  getPreviouslySelectedPayTypesFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.payTypesFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.payTypesFilter;
  }

  getPreviouslySelectedShiftRatingsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.shiftRatingsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.shiftRatingsFilter;
  }

  getPreviouslySelectedDayPartIdsFilter() {
    if (!Array.isArray(this.Authentication.user.clientcache.reports.dayPartIdsFilter)) {
      return [];
    }

    return this.Authentication.user.clientcache.reports.dayPartIdsFilter;
  }

  selectedFiltersChanged(filterProperties = [], clearFilter = false) {
    const filterPropArr = Array.isArray(filterProperties) ? filterProperties : [filterProperties];
    const clientcache = this.Authentication.user.clientcache;

    filterPropArr.forEach((field) => {
      if (clearFilter) {
        this.filter[field] = [];
      }

      clientcache.reports[`${field}Filter`] = [...this.filter[field]];
    });

    this.Authentication.syncClientCache();
    this.applyFilters();
  }

  syncFiltersCache() {
    const filterProperties = [
      'locationIds',
      'positionIds',
      'statusIds',
      'userIds',
      'leaveTypeIds',
      'groupIds',
      'tagIds',
      'payTypes',
      'isAutoClockOutIds',
    ];
    this.selectedFiltersChanged(filterProperties);
  }

  applyFilters() {
    console.error('ReportsBaseCtrl applyFilters method needs to be overriden by child class');
  }

  toggleFilter(...args) {
    _toggleFilter(this, ...args);
    this.syncFiltersCache();
  }

  get selectedDate() {
    return this._selectedDate;
  }

  set selectedDate(val) {
    this._selectedDate = val;
    if (!__HEADLESS__) {
      this.Authentication.user.clientcache.reports.selectedDate = val;
    }
    this.Authentication.syncClientCache();
    this.$location.search({ date: this.Utils.formatDate(val) });
  }

  get rangeMode() {
    if (!this._rangeMode) {
      this._rangeMode = this.Authentication.user.clientcache.reports.rangeMode;
      if (!this.modes.find((mode) => mode.value === this._rangeMode)) {
        this._rangeMode = 'week';
      }
    }

    return this._rangeMode;
  }

  set rangeMode(val) {
    this.setRangeMode(val);
  }

  get selectedRange() {
    return this._selectedRange;
  }

  set selectedRange(val = []) {
    this._selectedRange = val;
    this.Authentication.user.clientcache.reports.selectedRange = val;
    this.Authentication.syncClientCache();
    this.rangeStart = this.Utils.getRealDate(val[0]);
    this.rangeEnd = this.Utils.getRealDate(val[val.length - 1]);
    this.dataFromRange = null;
    this.refresh();
  }

  get rangeHeaderText() {
    const startFormatted = this.toDateMediumFilter(this.rangeStart);
    const endFormatted = this.toDateMediumFilter(this.rangeEnd);
    const rangeFormatted =
      startFormatted === endFormatted ? startFormatted : `${startFormatted} - ${endFormatted}`;

    return `${this.uppercaseFirstFilter(this.rangeMode)}: ${rangeFormatted}`;
  }

  setRangeMode(val) {
    const prevRangeMode = this._rangeMode;
    this._rangeMode = val;
    this.Authentication.user.clientcache.reports.rangeMode = val;
    this.Authentication.syncClientCache();

    if (prevRangeMode && prevRangeMode !== 'custom' && val === 'custom') {
      this.dateRangeSelectorOpened = true;
    }
  }

  getParams() {
    const { date } = this.$location.search();
    return { date };
  }

  getDataForExport() {
    const exportData = {
      header: [],
      body: [],
      footer: [],
    };

    const table = this.$document[0].getElementById('reports-table');

    const header = table.querySelector('thead').querySelector('tr');
    let cols = Array.from(header.querySelectorAll('th:not([skip-print])'));
    exportData.header.push(...cols.map((d) => this._getCellText(d).toUpperCase()));

    const mapSubcol = (index) => (subcol) => {
      if (!Array.isArray(subcol)) {
        return subcol;
      }

      if (index >= subcol.length) {
        return subcol[subcol.length - 1]; // pad
      }

      return subcol[index];
    };

    const hasMoreCols = (index) => (subcol) => Array.isArray(subcol) && index < subcol.length;

    const tbodys = Array.from(table.querySelectorAll('tbody'));
    const rows = tbodys.reduce(
      (allRows, tbody) => [...allRows, ...Array.from(tbody.querySelectorAll('tr:not(.ng-hide)'))],
      []
    );

    Array.from(rows).forEach((row) => {
      const subcols = [];
      cols = Array.from(row.querySelectorAll('td:not([skip-print])'));
      cols.forEach((col) => {
        const cells = Array.from(
          col.querySelectorAll('[ng-repeat*="data.userRows"]:not([skip-export])')
        );
        if (cells.length) {
          subcols.push(cells.map((d) => this._getCellText(d)));
        } else {
          subcols.push(this._getCellText(col));
        }
      });

      let i = 0;
      do {
        exportData.body.push(subcols.map(mapSubcol(i++)));
      } while (subcols.some(hasMoreCols(i)));
    });

    const footer = table.querySelector('tfoot')?.querySelector('tr');
    if (footer) {
      cols = Array.from(footer.querySelectorAll('td:not([skip-print])'));
      exportData.footer.push(...cols.map((d) => this._getCellText(d)));
    }

    return exportData;
  }

  _getCellText(cell) {
    const cellElement = angular.element(cell.outerHTML);
    Array.from(cellElement[0].querySelectorAll('[skip-print]')).forEach((hidden) =>
      hidden.remove()
    );
    return (cellElement[0].textContent || cellElement[0].innerText).trim();
  }

  refresh() {
    // can be overriden by child class
  }

  /**
   * Identifies the report request for a range, so concurrent requests only
   * supersede each other when they are for the same report (RF-35753).
   *
   * Reports must pass this to `Utils.raceProofRequest` instead of letting it
   * fall back to its shared 'default' slot, where any report could discard
   * another's response. `reportId` is set explicitly by each subclass rather
   * than derived from the class name, which Terser mangles in production.
   */
  getRaceProofRequestId(rangeStart, rangeEnd) {
    if (!this.reportId) {
      throw new Error('Report is missing a reportId, required to scope its requests');
    }

    return `${this.reportId}:${rangeStart}/${rangeEnd}`;
  }

  /**
   * Records the request about to be issued as this report's latest, and returns
   * its race-proof id. Reports call this instead of `getRaceProofRequestId` so
   * that `ownsLoadingState` can tell which settling request should release
   * `loading`.
   */
  trackRaceProofRequest(rangeStart, rangeEnd) {
    this._latestRequestId = this.getRaceProofRequestId(rangeStart, rangeEnd);

    return this._latestRequestId;
  }

  /**
   * True when the settling request is the most recent one this report issued,
   * and so is the one responsible for releasing `loading`.
   *
   * Range equality cannot answer that on its own: `LaborReportsCtrl` derives its
   * boundaries from `rangeMode` as well as `selectedRange`, and `setRangeMode`
   * does not refresh — so switching to Custom mid-flight changes what the
   * boundaries would be without issuing a request to take ownership of the flag,
   * stranding the spinner. Keying on the request actually issued keeps the flag
   * releasable there, while still stopping a superseded request from stealing it.
   */
  ownsLoadingState(rangeStart, rangeEnd) {
    return this._latestRequestId === this.getRaceProofRequestId(rangeStart, rangeEnd);
  }

  /**
   * True while `rangeStart`/`rangeEnd` are still the range the user has
   * selected.
   *
   * Reports check this after a fetch settles and drop the outcome when it is
   * false: without that check a response can be rendered under a heading for a
   * different range, because the heading updates synchronously from the date
   * selector while the data travels an async path (RF-35753). Scoping the race
   * key alone is not enough — two in-flight requests for different ranges no
   * longer evict each other, so a late-arriving older response would win.
   *
   * This governs rendering only. Whether a settling request may clear `loading`
   * is a separate question, answered by `ownsLoadingState` — a dropped response
   * still has to release the flag when no newer request exists to do it.
   */
  isRangeStillSelected(rangeStart, rangeEnd) {
    const range = this.selectedRange;

    if (!range || !range.length) {
      return false;
    }

    return range[0] === rangeStart && range[range.length - 1] === rangeEnd;
  }

  /**
   * True when a rejection is `Utils.raceProofRequest` discarding a superseded
   * request rather than a real failure. It throws a bare string, so there is no
   * error type to match on — keeping the literal in one place means a rename in
   * the SDK breaks one comparison instead of seven.
   */
  isSupersededRequestError(e) {
    return e === STALE_PROMISE_REJECTION;
  }

  get filtersOpened() {
    return this._filtersOpened;
  }

  set filtersOpened(val) {
    this._filtersOpened = val;
    this.Authentication.user.clientcache.reports.filtersOpened = val;
    this.Authentication.syncClientCache();
  }

  get statsOpened() {
    return this._statsOpened;
  }

  set statsOpened(val) {
    this._statsOpened = val;
    this.Authentication.user.clientcache.reports.statsOpened = val;
    this.Authentication.syncClientCache();
  }

  refreshUserOptions(otherUsers = [], users) {
    this.users = users || this.PersonaCollection.users;

    if (otherUsers.length) {
      this.users = this.Utils.uniq([...this.users, ...otherUsers], (u) => u.id);
    }

    this.userOptions = this.Utils.sortBy(this.users, 'fullname').map((user) => ({
      id: user.id,
      name: user.fullname,
      fullname: user.fullname,
    }));
  }

  handleArchivedGroups = (event) => {
    if (!this._locationsDict[event.location.id]) {
      this.locations.push(event.location);
      this._locationsDict[event.location.id] = true;
    }

    if (!this._positionsDict[event.position.id]) {
      this.positions.push(event.position);
      this._positionsDict[event.position.id] = true;
    }
  };
}

export default ReportsBaseCtrl;
